// Lógica pura da janela "Feriados" (Administrativo › Horas da equipe, 13/10/2026)
// — sem React, sem antd, sem rede: dá para testar no jest do CRA.
//
// Quem decide o que é feriado é o servidor (dre-engine/handlers/feriadosHoras.js):
// o que vira dia cadastrado, o que fica de fora (fim de semana, feriado nacional)
// e os períodos da lista. Aqui só se escreve a frase para gente.

/** Uma linha do calendário do ano, como vem de GET /financeiro/painel/feriados. */
export type FeriadoDoAno = {
  inicio: string;
  fim: string;
  dias: number;
  datas: string[];
  nome: string;
  /** 'nacional' = data fixa, vem da configuração e não se remove por aqui. */
  origem: 'nacional' | 'cadastrado';
  /** E-mail de quem cadastrou (só nos cadastrados). */
  por: string | null;
  /** Feriado nacional que caiu em sábado ou domingo: aparece, mas não muda nada. */
  fim_de_semana: boolean;
};

export type CalendarioDoAno = { ano: number; hoje: string; pode_editar: boolean; feriados: FeriadoDoAno[] };

/** Resposta de POST /financeiro/painel/feriados. */
export type RespostaCadastro = {
  nome: string;
  cadastrados: string[];
  renomeados: string[];
  pulados: { data: string; motivo: 'fim_de_semana' | 'feriado_nacional' | 'ja_cadastrado'; nome?: string }[];
  com_horas: { data: string; pessoas: number }[];
};

/** Nomes que a pessoa mais vai digitar — sugestão, não lista fechada. */
export const SUGESTOES_DE_NOME = [
  'Carnaval', 'Quarta-feira de Cinzas', 'Sexta-feira Santa', 'Corpus Christi', 'Revolução Farroupilha',
  'Nossa Senhora dos Navegantes', 'Feriado municipal', 'Ponto facultativo', 'Emenda de feriado', 'Recesso de fim de ano',
];

const RE_ISO = /^\d{4}-\d{2}-\d{2}$/;
const DIAS = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];
const DIAS_CURTOS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
const dow = (iso: string) => new Date(`${iso}T12:00:00Z`).getUTCDay();
const diaMes = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
const plural = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;

/** "seg 02/11" · "qui 24/12 a ter 29/12". */
export function quandoDoFeriado(f: Pick<FeriadoDoAno, 'inicio' | 'fim'>): string {
  const um = (iso: string) => `${DIAS_CURTOS[dow(iso)]} ${diaMes(iso)}`;
  return f.inicio === f.fim ? um(f.inicio) : `${um(f.inicio)} a ${um(f.fim)}`;
}

/** O que acompanha o nome na lista: de onde veio e, se for o caso, por que não conta. */
export function detalheDoFeriado(f: FeriadoDoAno): string {
  if (f.origem === 'nacional') return f.fim_de_semana ? `nacional · cai no ${DIAS[dow(f.inicio)]}` : 'nacional';
  const quem = f.por ? ` por ${String(f.por).split('@')[0]}` : '';
  return `cadastrado${quem}${f.dias > 1 ? ` · ${f.dias} dias` : ''}`;
}

/** Já passou? (para a lista esmaecer o que ficou para trás.) */
export const jaPassou = (f: Pick<FeriadoDoAno, 'fim'>, hoje: string): boolean => f.fim < hoje;

/** O próximo feriado em dia de semana, de hoje em diante — o que a tela destaca. */
export function proximoFeriado(lista: FeriadoDoAno[], hoje: string): FeriadoDoAno | null {
  return lista.filter(f => !f.fim_de_semana && f.fim >= hoje).sort((a, b) => a.inicio.localeCompare(b.inicio))[0] || null;
}

/** Quantos dias de segunda a sexta há no período (prévia: feriado nacional quem tira é o servidor). */
export function diasDeSemana(inicio: string, fim: string): number {
  if (!RE_ISO.test(inicio) || !RE_ISO.test(fim) || fim < inicio) return 0;
  let n = 0;
  const d = new Date(`${inicio}T12:00:00Z`);
  const ultimo = new Date(`${fim}T12:00:00Z`);
  for (; d <= ultimo; d.setUTCDate(d.getUTCDate() + 1)) if (d.getUTCDay() !== 0 && d.getUTCDay() !== 6) n++;
  return n;
}

/** A prévia embaixo das datas, antes de cadastrar. */
export function previaDoPeriodo(inicio: string, fim: string): string {
  const n = diasDeSemana(inicio, fim);
  if (!n) return 'Esse período só tem fim de semana — fim de semana já não conta como dia de trabalho.';
  // Um dia só: aqui ele é de segunda a sexta (sábado e domingo saíram acima).
  return inicio === fim
    ? `Cai numa ${DIAS[dow(inicio)]}-feira.`
    : `${plural(n, 'dia de semana', 'dias de semana')} no período. Fim de semana e feriado nacional ficam de fora.`;
}

/** O que dizer depois de cadastrar: o que entrou e, se for o caso, o que já tinha hora lançada. */
export function frasesDoCadastro(r: RespostaCadastro): { ok: string; aviso: string | null } {
  const dias = [...(r.cadastrados || []), ...(r.renomeados || [])].sort();
  const quando = dias.length === 1 ? diaMes(dias[0]) : `${diaMes(dias[0])} a ${diaMes(dias[dias.length - 1])}`;
  const ok = `${r.nome}: ${plural(dias.length, 'dia cadastrado', 'dias cadastrados')} (${quando}). Ninguém é cobrado por ${dias.length === 1 ? 'ele' : 'eles'}.`;
  const comHoras = r.com_horas || [];
  if (!comHoras.length) return { ok, aviso: null };
  const partes = comHoras.map(c => `${diaMes(c.data)} (${plural(c.pessoas, 'pessoa', 'pessoas')})`);
  return { ok, aviso: `Já havia hora lançada em ${partes.join(', ')}: as horas continuam valendo normalmente.` };
}

/** O texto da confirmação de remover. */
export function perguntaDeRemover(f: FeriadoDoAno): string {
  return `Remover "${f.nome}" (${quandoDoFeriado(f)})?`;
}
